import {
	Action, Module, Mutation, VuexModule,
} from 'vuex-module-decorators';
import { ProfileFI } from '@/profile/types/profile-fi';
import { ProfileAccount } from '@/profile/types/profile-account';
import { AccountType } from '@/core/enums/account-type';
import { Borrower } from '@/core/types/borrower';
import { ProfileAPIService } from '@/profile/services/api';
import { ProfileLinkedAccount } from '@/profile/types/profile-linked-account';
import { ProfileHelper } from '@/profile/helper';

@Module({
	namespaced: true,
	name: 'profile',
})
export default class ProfileStore extends VuexModule {
	public accounts: ProfileAccount[] = [];

	public institutions: ProfileFI[] = [];

	public isPageErrorShown = false;

	public isPageLoaderShown = false;

	public loanLinkedAccounts: ProfileLinkedAccount[] = [];

	public loanBorrowers: Borrower[] = [];

	public selectedInstitution: ProfileFI | null = null;

	get hasLoanAccounts(): boolean {
		return this.accounts.some((account) => account.kloansType === AccountType.LOAN);
	}

	@Mutation
	public setAccounts(accounts: ProfileAccount[]): void {
		this.accounts = accounts;
	}

	@Mutation
	public setInstitutions(institutions: ProfileFI[]): void {
		this.institutions = institutions;
	}

	@Mutation
	public setIsPageErrorShown(isShown: boolean): void {
		this.isPageErrorShown = isShown;
	}

	@Mutation
	public setIsPageLoaderShown(isShown: boolean): void {
		this.isPageLoaderShown = isShown;
	}

	@Mutation
	public setLoanLinkedAccounts(accounts: ProfileLinkedAccount[]): void {
		this.loanLinkedAccounts = accounts;
	}

	@Mutation
	public setLoanBorrowers(borrowers: Borrower[]): void {
		this.loanBorrowers = borrowers;
	}

	@Mutation
	public setSelectedInstitution(institution: ProfileFI | null): void {
		this.selectedInstitution = institution;
	}

	@Action({ rawError: true })
	public setInstitutionsFromAccounts(): void {
		const { accounts } = this;
		const institutions = ProfileHelper.getInstitutionsFromAccounts({ accounts });

		this.setInstitutions(institutions);
	}

	@Action({ rawError: true })
	public setDefaultSelectedInstitution(): void {
		const { institutions, accounts } = this;
		const defaultAccount = ProfileHelper.getDefaultAccount(accounts);
		const defaultInstitution = institutions.find((i) => i.id === defaultAccount?.fi.id);

		this.setSelectedInstitution(defaultInstitution ?? null);
	}

	@Action({ rawError: true })
	public async getAndSetAccounts(): Promise<void> {
		const accounts = await ProfileAPIService.getAccounts();

		this.setAccounts(accounts);
	}

	@Action({ rawError: true })
	public async getAndSetLinkedAccounts(): Promise<void> {
		const [loanLinkedAccounts] = await Promise.all([
			ProfileAPIService.getBankAccountsByProduct(AccountType.LOAN),
		]);

		this.setLoanLinkedAccounts(loanLinkedAccounts);
	}

	@Action({ rawError: true })
	public async getAndSetAllAccounts(): Promise<void> {
		await Promise.all([
			this.getAndSetAccounts(),
			this.getAndSetLinkedAccounts(),
		]);
	}
}
